import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@store/index';
import { selectUser } from '@features/auth/authSlice';
import { displayNameOf, greetingSlotAt } from '@utils/greeting';

interface Greeting {
  /** "おはようございます、田中さん" — or the same without the name. */
  message: string;
  name: string;
  initial: string;
}

/**
 * Resolved once per render rather than on a timer. The slot only matters at the
 * moment the page opens, and ticking a clock to re-greet a user mid-session
 * would redraw the header for no one's benefit.
 */
export function useGreeting(): Greeting {
  const { t } = useTranslation();
  const user = useAppSelector(selectUser);
  const name = displayNameOf(user);

  return useMemo(() => {
    const slot = greetingSlotAt(new Date());
    return {
      message: name
        ? t(`dashboard.greeting.${slot}Named`, { name })
        : t(`dashboard.greeting.${slot}`),
      name,
      initial: name.charAt(0).toUpperCase(),
    };
  }, [t, name]);
}
